import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Brand } from "@/components/shared/Common";
import { useAuth } from "@/lib/auth.jsx";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV = [
  { to: "/find-care", label: "Find Care", testid: "nav-find-care" },
  { to: "/doctors", label: "Doctors", testid: "nav-doctors" },
  { to: "/specialists", label: "Specialists", testid: "nav-specialists" },
  { to: "/how-it-works", label: "How It Works", testid: "nav-how-it-works" },
  { to: "/for-doctors", label: "For Doctors", testid: "nav-for-doctors" },
  { to: "/pricing", label: "Pricing", testid: "nav-pricing" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const dashboardHref = user?.role === "doctor" ? "/doctor" : user?.role === "admin" ? "/admin" : "/patient";
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#e2e8f0]" data-testid="site-header">
      <div className="cm-container flex items-center justify-between h-16">
        <Link to="/" data-testid="header-brand-link"><Brand /></Link>
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) => `px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? "text-[#1a2b4a] bg-[#ebf3fc]" : "text-[#475569] hover:text-[#1a2b4a] hover:bg-[#f5f7fa]"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              <Link to={dashboardHref} className="cm-btn-outline" data-testid="header-dashboard-link">Dashboard</Link>
              <button onClick={logout} className="cm-btn-primary" data-testid="header-logout-btn">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-semibold text-[#1a2b4a] hover:text-[#4a90d9] px-3 py-2" data-testid="header-login-link">Sign in</Link>
              <Link to="/register" className="cm-btn-primary" data-testid="header-signup-link">Create account</Link>
            </>
          )}
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="header-mobile-toggle" aria-label="menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#e2e8f0] bg-white">
          <div className="cm-container py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-sm text-[#1a2b4a] hover:bg-[#f5f7fa]" data-testid={`mobile-${n.testid}`}>{n.label}</Link>
            ))}
            <div className="border-t border-[#e2e8f0] my-2" />
            {user ? (
              <>
                <Link to={dashboardHref} onClick={() => setOpen(false)} className="cm-btn-outline w-full" data-testid="mobile-dashboard-link">Dashboard</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="cm-btn-primary w-full mt-2" data-testid="mobile-logout-btn">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="cm-btn-outline w-full" data-testid="mobile-login-link">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="cm-btn-primary w-full mt-2" data-testid="mobile-signup-link">Create account</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#1a2b4a] text-white/80 mt-20">
    <div className="cm-container py-16 grid md:grid-cols-4 gap-10">
      <div>
        <div className="text-white text-xl font-extrabold tracking-tight mb-3">
          <span className="text-[#4a90d9]">CARE</span>Match
        </div>
        <p className="text-sm text-white/60 leading-relaxed">The right care, made simpler. AI-assisted healthcare navigation with verified professionals.</p>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-white mb-3">Product</h4>
        <ul className="space-y-2 text-sm">
          <li><Link to="/find-care" className="hover:text-white">Find Care</Link></li>
          <li><Link to="/doctors" className="hover:text-white">Doctors</Link></li>
          <li><Link to="/specialists" className="hover:text-white">Specialists</Link></li>
          <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
          <li><Link to="/health-info" className="hover:text-white">Health Information</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-white mb-3">Company</h4>
        <ul className="space-y-2 text-sm">
          <li><Link to="/about" className="hover:text-white">About</Link></li>
          <li><Link to="/trust-safety" className="hover:text-white">Trust &amp; Safety</Link></li>
          <li><Link to="/for-doctors" className="hover:text-white">For Doctors</Link></li>
          <li><Link to="/help" className="hover:text-white">Help Centre</Link></li>
          <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-white mb-3">Legal</h4>
        <ul className="space-y-2 text-sm">
          <li><Link to="/legal/privacy" className="hover:text-white">Privacy Policy</Link></li>
          <li><Link to="/legal/terms" className="hover:text-white">Terms of Service</Link></li>
          <li><Link to="/legal/patient-terms" className="hover:text-white">Patient Terms</Link></li>
          <li><Link to="/legal/doctor-terms" className="hover:text-white">Doctor Terms</Link></li>
          <li><Link to="/legal/cookies" className="hover:text-white">Cookie Policy</Link></li>
          <li><Link to="/legal/ai-transparency" className="hover:text-white">AI Transparency</Link></li>
          <li><Link to="/legal/accessibility" className="hover:text-white">Accessibility</Link></li>
          <li><Link to="/legal/emergency" className="hover:text-white">Emergency Information</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="cm-container py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/50">
        <div>© {new Date().getFullYear()} CAREMatch. All rights reserved.</div>
        <div>CAREMatch is not for medical emergencies. Call 999 if urgent.</div>
      </div>
    </div>
  </footer>
);

export default function PublicLayout() {
  const location = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
